import {
  takeLatest, put, call, all, select,
} from 'redux-saga/effects';
import history from '@rootCommon/utilities/history';
import { ROUTES } from '@common/constants/routes';
import SitesService from '@services/sites';
import * as actions from '@actions';
import { RootState } from '@reducers';
import { IdType } from '@rootCommon/types/common';
import {
  Site, SiteDto, SiteSelector, SitesListItem,
} from '@rootCommon/types/site';
import { Filters } from '@rootCommon/types/filter';

import { AxiosError } from 'axios';
import { SagaGenerator } from './index';

// fetch all
function* fetchSitesAsync() {
  try {
    const filters: Filters = yield select((state: RootState) => state.sitesList.filter);
    const sites: SitesListItem[] = yield call(SitesService.getList, filters);

    yield put(actions.fetchSites.success(sites));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchSites.failure(error));
  }
}

function* watchFetchSites() {
  yield takeLatest(actions.fetchSites.request, fetchSitesAsync);
}

// fetch selector
function* fetchSitesSelectorAsync() {
  try {
    const sites: SiteSelector[] = yield call(SitesService.getSelectorList);

    yield put(actions.fetchSitesSelector.success(sites));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchSitesSelector.failure(error));
  }
}

function* watchFetchSitesSelector() {
  yield takeLatest(actions.fetchSitesSelector.request, fetchSitesSelectorAsync);
}

// fetch item
function* fetchSiteAsync(action: { payload: IdType }) {
  try {
    const site: Site = yield call(SitesService.getById, action.payload);

    yield put(actions.fetchSite.success(site));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchSite.failure(error));
  }
}

function* watchFetchSite() {
  yield takeLatest(actions.fetchSite.request, fetchSiteAsync);
}

// create
function* createSiteAsync(action: { payload: SiteDto }) {
  try {
    const site: Site = yield call(SitesService.create, action.payload);

    yield put(actions.createSite.success(site));
    yield put(actions.fetchSites.request());
    history.push(ROUTES.SITES.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.createSite.failure(error));
  }
}

function* watchCreateSite() {
  yield takeLatest(actions.createSite.request, createSiteAsync);
}

// update
function* updateSiteAsync(action: { payload: { id: IdType; data: SiteDto } }) {
  try {
    const site: Site = yield call(SitesService.update, action.payload.id, action.payload.data);

    yield put(actions.updateSite.success(site));
    yield put(actions.fetchSites.request());
    history.push(ROUTES.SITES.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.updateSite.failure(error));
  }
}

function* watchUpdateSite() {
  yield takeLatest(actions.updateSite.request, updateSiteAsync);
}

// delete
function* deleteSiteAsync(action: { payload: IdType }) {
  try {
    yield call(SitesService.delete, action.payload);
    yield put(actions.deleteSite.success(action.payload));
    history.push(ROUTES.SITES.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.deleteSite.failure(error));
  }
}

function* watchDeleteSite() {
  yield takeLatest(actions.deleteSite.request, deleteSiteAsync);
}

// filters
function* setSitesFilterAsync() {
  yield put(actions.fetchSites.request());
}

function* watchFilterSites() {
  yield takeLatest(actions.setSitesFilter, setSitesFilterAsync);
}

function* watchSites(): SagaGenerator {
  yield all([
    watchFetchSites(),
    watchFetchSitesSelector(),
    watchFetchSite(),
    watchCreateSite(),
    watchUpdateSite(),
    watchDeleteSite(),
    watchFilterSites(),
  ]);
}

export default watchSites;
